import React, { useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, LayersIcon } from 'lucide-react';
import { loan } from '../../data/loan';
import { providers } from '../../data/providers';
import type { ProviderId, RunMode } from '../../types/aus';
import { AdvancedOptions } from './AdvancedOptions';
import { CreditCard } from './CreditCard';
import { CredentialStatusRow } from './CredentialStatusRow';
import { FannieSubmissionCards } from './FannieSubmissionCards';
import { FreddieSubmissionCards } from './FreddieSubmissionCards';
import { MissingInfoCard } from './MissingInfoCard';
import { PreflightChecklist } from './PreflightChecklist';
import { ProviderMark } from './ProviderMark';
interface ReviewScreenProps {
  mode: RunMode;
  creditReady: boolean;
  credentialsReady: boolean;
  onResolveCredit: () => void;
  onResolveCredentials: () => void;
  onBack: () => void;
  onSubmit: () => void;
}
export function ReviewScreen({
  mode,
  creditReady,
  credentialsReady,
  onResolveCredit,
  onResolveCredentials,
  onBack,
  onSubmit
}: ReviewScreenProps) {
  const isDual = mode === 'dual';
  const isFreddie = mode === 'freddie';
  const singleProvider: ProviderId = mode === 'dual' ? 'fannie' : mode;
  const config = providers[singleProvider];
  const [skipReissue, setSkipReissue] = useState(false);
  const [isResubmission, setIsResubmission] = useState(false);
  const [lpaKeyNumber, setLpaKeyNumber] = useState('');
  const ready = creditReady && credentialsReady;
  const title = isDual ? 'Dual AUS Run' : `${config.name} ${config.product}`;
  const supportingText = isDual ? 'Both engines will be submitted back to back using the loan and credit information already stored on this loan.' : isFreddie ? 'Loan, credit, and authentication information have been populated automatically.' : 'We’ve populated the submission using information already stored on this loan.';
  const submitLabel = isDual ? 'Submit to Both Engines' : config.submitLabel;
  const credentialNames = isDual ? 'Fannie Mae DU / DO · Freddie Mac LPA' : `${config.name} ${config.shortLabel}`;
  const buttonClasses = isDual ? 'bg-brand-700 hover:bg-brand-800 focus-visible:ring-brand-700' : `${config.accentSolid} ${config.accentSolidHover} ${config.accentRing}`;
  return <div className="space-y-4" data-id="element-252" data-mp="322xpbxnrtc8r">
      <header className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm" data-id="element-253" data-mp="2x2t8g6iyafi0">
        <div className="flex flex-wrap items-start gap-4" data-id="element-254" data-mp="3uqbe38hbf1hs">
          {isDual ? <span className="flex items-center" data-id="element-255" data-mp="3pqyucwlqqajf">
              <ProviderMark provider="fannie" size="md" data-id="element-256" data-mp="3pr78ertlnlw7" />
              <span className="-ml-3 rounded-xl ring-2 ring-white" data-id="element-257" data-mp="3kzgt07ft27xx">
                <ProviderMark provider="freddie" size="md" data-id="element-258" data-mp="3kxm6ioovy7rq" />
              </span>
            </span> : <ProviderMark provider={singleProvider} size="md" data-id="element-259" data-mp="havmoqumolv3" />}
          <div className="min-w-0 flex-1" data-id="element-260" data-mp="hcyl9z9ujnbf">
            <div className="flex flex-wrap items-center gap-2.5" data-id="element-261" data-mp="cjnbpqdngsft">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold tracking-tight text-graphite-800" data-id="element-262" data-mp="cic6rn8gxen4">
                {isDual && <LayersIcon aria-hidden="true" className="h-4 w-4 text-brand-700" data-id="element-263" data-mp="otv4is2fy5cc" />}
                {title}
              </h2>
              {ready ? <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700" data-id="element-264" data-mp="tvkgqb1egp20">
                  Ready to Submit
                </span> : <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700" data-id="element-265" data-mp="13rf4tloe7cc6">
                  Action Required
                </span>}
            </div>
            {isDual && <p className="mt-1 text-sm font-medium text-slate-600" data-id="element-266" data-mp="1cmbpbadkcbfc">
                Fannie Mae Desktop Underwriter + Freddie Mac Loan Product Advisor
              </p>}
            <p className="mt-1 text-sm text-slate-500" data-id="element-267" data-mp="1mg2941yqvxop">{supportingText}</p>
          </div>
        </div>
      </header>

      {!creditReady && <MissingInfoCard title="Credit information required" description="We couldn't locate a reusable credit report for this borrower." actionLabel="Select Credit Report" onAction={onResolveCredit} data-id="element-268" data-mp="2o83jitx8hhje" />}
      {!credentialsReady && <MissingInfoCard title="AUS credentials required" description={isDual ? 'Connect your Fannie Mae and Freddie Mac credentials before submitting this loan.' : `Connect your ${config.name} credentials before submitting this loan.`} actionLabel="Connect Credentials" onAction={onResolveCredentials} data-id="element-269" data-mp="2szlen1cksnwg" />}

      {!isFreddie && <FannieSubmissionCards data-id="element-270" data-mp="2a7srvoo3nyx6" />}
      {(isFreddie || isDual) && <FreddieSubmissionCards data-id="element-271" data-mp="3ppm28x17kyc6" />}

      {creditReady && <CreditCard mode={mode} onEditReference={onResolveCredit} data-id="element-272" data-mp="3pob35aipkere" />}

      <CredentialStatusRow providerName={credentialNames} connected={credentialsReady} onManage={onResolveCredentials} data-id="element-273" data-mp="3lok30g7akma7" />

      <AdvancedOptions mode={mode} skipReissue={skipReissue} onSkipReissueChange={setSkipReissue} isResubmission={isResubmission} onResubmissionChange={setIsResubmission} lpaKeyNumber={lpaKeyNumber} onLpaKeyNumberChange={setLpaKeyNumber} onEditReference={onResolveCredit} onManageCredentials={onResolveCredentials} data-id="element-274" data-mp="3t9mk7s25db8u" />

      <div className="sticky bottom-4 z-10 rounded-2xl border border-slate-200 bg-white/95 px-5 py-4 shadow-lg backdrop-blur" data-id="element-275" data-mp="ftf32g8jns3s">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-276" data-mp="62tx27qjjlau">
          <PreflightChecklist items={[{
          label: 'Loan information complete',
          ready: true
        }, {
          label: 'Credit reference found',
          ready: creditReady
        }, {
          label: 'AUS credentials connected',
          ready: credentialsReady
        }]} data-id="element-277" data-mp="b1x41xq301lg" />
          <div className="flex items-center gap-2" data-id="element-278" data-mp="13k2idmkshk0d">
            <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900" data-id="element-279" data-mp="1dcwx0hnp9c63">
              <ArrowLeftIcon className="h-4 w-4" data-id="element-280" data-mp="zgrquvzn480p" />
              Back
            </button>
            <button type="button" onClick={onSubmit} disabled={!ready} className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 ${buttonClasses}`} data-id="element-281" data-mp="uhf7wmbw3l3e">
              {submitLabel}
              <ArrowRightIcon className="h-4 w-4" data-id="element-282" data-mp="6q1e0by3bhgb" />
            </button>
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-400 lg:hidden" data-id="element-283" data-mp="2idd9xgql7jju">
          {loan.borrower} · Loan #{loan.loanNumber}
        </p>
      </div>
    </div>;
}